import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ArticleHero from './components/ArticleHero';
import ArticleContent from './components/ArticleContent';
import AuthorBio from './components/AuthorBio';
import RelatedPosts from './components/RelatedPosts';
import LandingFooter from '../nord-pass-landing-page/components/LandingFooter';
import Breadcrumb from '../../components/Breadcrumb';
import LegalResourcesSidebar from './components/LegalResourcesSidebar';
import CommentThread from './components/CommentThread';

const IndividualArticleDetailView = () => {
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readProgress, setReadProgress] = useState(0);
  const contentRef = useRef(null);

  const slug = new URLSearchParams(location?.search)?.get('slug');

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
    } else {
      fetchLatestPost();
    }
    fetchAllPosts();
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef?.current) return;
      const el = contentRef?.current;
      const rect = el?.getBoundingClientRect();
      const total = el?.offsetHeight;
      const scrolled = Math.max(0, -rect?.top);
      const progress = Math.min(100, Math.round((scrolled / total) * 100));
      setReadProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchPost = async (postSlug) => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase?.from('blog_posts')?.select('*')?.eq('slug', postSlug)?.single();
      if (fetchError) throw fetchError;
      setPost(data);
    } catch (err) {
      setError(err?.message || 'Kunne ikke laste artikkelen');
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase?.from('blog_posts')?.select('*')?.order('published_at', { ascending: false })?.limit(1)?.single();
      if (fetchError) throw fetchError;
      setPost(data);
    } catch (err) {
      setError(err?.message || 'Kunne ikke laste artikkelen');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPosts = async () => {
    try {
      const { data } = await supabase?.from('blog_posts')?.select('id, title, slug, hero_image, hero_image_alt, category, published_at, author_name')?.order('published_at', { ascending: false })?.limit(10);
      setAllPosts(data || []);
    } catch {}
  };

  const CATEGORY_COLORS = {
    'Nyheter': '#3B6FD4',
    'Veioppdateringer': '#2A9D8F',
    'Tips & Råd': '#E9C46A',
    'Teknologi': '#9B5DE5',
    'Sikkerhet': '#E76F51'
  };

  const LEGAL_CATEGORIES = ['Sikkerhet', 'Personvern', 'GDPR', 'Teknologi'];
  const showLegalSidebar = post?.category && LEGAL_CATEGORIES?.includes(post?.category);

  return (
    <>
      <Helmet>
        <title>{post?.title ? `${post?.title} — NordPass` : 'Artikkel — NordPass'}</title>
        <meta name="description" content={post?.excerpt || 'Les siste nyheter og artikler fra NordPass.'} />
        <link
          href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Inter:wght@300;400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div style={{ background: '#050A12', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* Reading progress bar */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '3px',
            width: `${readProgress}%`,
            background: 'linear-gradient(90deg, #3B6FD4, #A8C4F0)',
            zIndex: 200,
            transition: 'width 0.1s ease'
          }}
        />

        {/* Navigation */}
        <nav
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'rgba(5,10,18,0.95)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 5%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
            <a href="/nord-pass-landing-page" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <div style={{ width: '32px', height: '32px', background: '#3B6FD4', clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }} />
              <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.125rem', fontWeight: 800, color: '#EEF0F2', letterSpacing: '-0.03em' }}>NordPass</span>
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <a href="/blog-news-landing-page-blogg-nyheter" style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: 'rgba(238,240,242,0.65)', textDecoration: 'none' }}>← Tilbake til Blogg</a>
              <a href="/nord-pass-contact-page" style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: 'rgba(238,240,242,0.65)', textDecoration: 'none' }}>Kontakt</a>
            </div>
          </div>
        </nav>

        {/* Breadcrumb */}
        {!loading && !error && post && (
          <Breadcrumb
            crumbs={[
              { label: 'Hjem', href: '/nord-pass-landing-page' },
              { label: 'Blogg', href: '/blog-news-landing-page-blogg-nyheter' },
              { label: post?.category || 'Artikkel', href: '/blog-news-landing-page-blogg-nyheter' },
              { label: post?.title?.length > 40 ? post?.title?.slice(0, 40) + '…' : post?.title }
            ]}
          />
        )}

        {/* Loading state */}
        {loading && (
          <div style={{ marginTop: '72px', height: '600px', background: '#0D1520', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Impact, sans-serif', fontSize: '3rem', color: 'rgba(255,255,255,0.06)', marginBottom: '16px' }}>LASTER...</div>
              <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(238,240,242,0.4)' }}>Henter artikkel</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div style={{ marginTop: '72px', padding: '120px 6%', textAlign: 'center' }}>
            <p style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.2rem', color: '#E76F51', fontWeight: 700 }}>Artikkelen ble ikke funnet</p>
            <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(238,240,242,0.5)', marginTop: '8px' }}>{error}</p>
            <a href="/blog-news-landing-page-blogg-nyheter" style={{ display: 'inline-block', marginTop: '24px', fontFamily: 'Syne, sans-serif', fontSize: '0.8rem', fontWeight: 700, color: '#3B6FD4', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase' }}>← Tilbake til Blogg</a>
          </div>
        )}

        {/* Article content */}
        {!loading && !error && post && (
          <>
            <ArticleHero post={post} />

            <div ref={contentRef} style={{ maxWidth: '1200px', margin: '0 auto', padding: '64px 6%' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '64px', alignItems: 'start' }}>
                {/* Main content */}
                <div>
                  {/* Decorative drop cap area */}
                  <div
                    style={{
                      borderLeft: `4px solid ${CATEGORY_COLORS?.[post?.category] || '#3B6FD4'}`,
                      paddingLeft: '24px',
                      marginBottom: '40px'
                    }}
                  >
                    <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '1.15rem', color: 'rgba(238,240,242,0.75)', lineHeight: 1.7, margin: 0 }}>
                      {post?.excerpt}
                    </p>
                  </div>

                  {/* Inline cross-reference banner for privacy/legal articles */}
                  {showLegalSidebar && (
                    <div
                      style={{
                        background: 'rgba(59,111,212,0.06)',
                        border: '1px solid rgba(59,111,212,0.2)',
                        borderLeft: '4px solid #3B6FD4',
                        padding: '16px 20px',
                        marginBottom: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap'
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'JetBrains Mono, monospace',
                          fontSize: '0.625rem',
                          color: '#3B6FD4',
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          flexShrink: 0
                        }}
                      >
                        JURIDISK INFO
                      </span>
                      <p
                        style={{
                          fontFamily: 'Georgia, serif',
                          fontStyle: 'italic',
                          fontSize: '0.875rem',
                          color: 'rgba(238,240,242,0.65)',
                          margin: 0,
                          flex: 1
                        }}
                      >
                        Se vår{' '}
                        <a
                          href="/privacy-policy-personvern"
                          style={{ color: '#3B6FD4', textDecoration: 'underline', textDecorationColor: 'rgba(59,111,212,0.4)' }}
                        >
                          Personvernerklæring
                        </a>{' '}for informasjon om databehandling, og{' '}
                        <a
                          href="/terms-conditions-vilk-r-betingelser"
                          style={{ color: '#F97316', textDecoration: 'underline', textDecorationColor: 'rgba(249,115,22,0.4)' }}
                        >
                          Vilkår & Betingelser
                        </a>{' '}for abonnementsdetaljer.
                      </p>
                    </div>
                  )}

                  <ArticleContent content={post?.content} />
                  <AuthorBio post={post} />
                  <CommentThread postId={post?.id} />
                  <RelatedPosts posts={allPosts} currentSlug={post?.slug} />
                </div>

                {/* Sidebar */}
                <aside style={{ position: 'sticky', top: '96px' }}>
                  {/* Share section */}
                  <div
                    style={{
                      background: '#0D1520',
                      border: '1px solid rgba(255,255,255,0.07)',
                      padding: '24px',
                      marginBottom: '24px',
                      transform: 'rotate(0.5deg)'
                    }}
                  >
                    <p style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#3B6FD4', letterSpacing: '0.25em', textTransform: 'uppercase', margin: '0 0 16px' }}>Del artikkel</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { label: 'Del på Facebook', icon: 'f' },
                        { label: 'Del på Twitter/X', icon: 'x' },
                        { label: 'Kopier lenke', icon: '⧉' }
                      ]?.map((btn) => (
                        <button
                          key={btn?.label}
                          onClick={() => { if (btn?.icon === '⧉') { navigator?.clipboard?.writeText?.(window?.location?.href); } }}
                          style={{
                            fontFamily: 'Syne, sans-serif',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: 'rgba(238,240,242,0.65)',
                            background: 'transparent',
                            border: '1px solid rgba(255,255,255,0.1)',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            letterSpacing: '0.05em',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#3B6FD4'; e.currentTarget.style.color = '#EEF0F2'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(238,240,242,0.65)'; }}
                        >
                          {btn?.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reading progress */}
                  <div
                    style={{
                      background: '#0D1520',
                      border: '1px solid rgba(255,255,255,0.07)',
                      padding: '24px',
                      transform: 'rotate(-0.4deg)'
                    }}
                  >
                    <p style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#3B6FD4', letterSpacing: '0.25em', textTransform: 'uppercase', margin: '0 0 12px' }}>Lesefremdrift</p>
                    <div style={{ background: 'rgba(255,255,255,0.08)', height: '4px', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${readProgress}%`, background: 'linear-gradient(90deg, #3B6FD4, #A8C4F0)', transition: 'width 0.2s ease' }} />
                    </div>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: 'rgba(238,240,242,0.4)', marginTop: '8px' }}>{readProgress}% lest</p>
                  </div>

                  {/* Legal Resources Sidebar — always shown */}
                  <LegalResourcesSidebar />
                </aside>
              </div>
            </div>
          </>
        )}

        <LandingFooter />
      </div>
    </>
  );
};

export default IndividualArticleDetailView;
